import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Card, CardHeader } from '@/components/ui/card';
import PathGraph from '@/components/PathGraph';

interface PathPreviewProps {
  pathId: string;
  title: string;
  onClick: () => void;
}

const PathPreview: React.FC<PathPreviewProps> = ({ pathId, title, onClick }) => {
  const { data: path } = useQuery({
    queryKey: ['path-meta', pathId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('paths')
        .select('cover_image_url')
        .eq('id', pathId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const { data: nodes = [] } = useQuery({
    queryKey: ['path-preview-nodes', pathId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('path_nodes')
        .select('*')
        .eq('path_id', pathId);
      if (error) throw error;
      return data;
    },
  });

  const { data: collections = [] } = useQuery({
    queryKey: ['path-preview-collections', pathId],
    queryFn: async () => {
      const { data, error } = await supabase.from('collections').select('*');
      if (error) throw error;
      return data;
    },
  });

  const { data: collectionVideos = [] } = useQuery({
    queryKey: ['path-preview-videos', pathId],
    queryFn: async () => {
      const collectionIds = nodes.map((n) => n.collection_id).filter(Boolean);
      if (!collectionIds.length) return [];
      const { data, error } = await supabase
        .from('collection_videos')
        .select('*')
        .in('collection_id', collectionIds);
      if (error) throw error;
      return data;
    },
    enabled: nodes.length > 0,
  });

  return (
    <Card
      className="cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-lg overflow-hidden"
      onClick={onClick}
    >
      {path?.cover_image_url ? (
        <img
          src={path.cover_image_url}
          alt="Cover"
          className="w-full h-[200px] object-cover"
        />
      ) : nodes.length > 0 ? (
        <div className="relative h-[200px] bg-background/50 pointer-events-none select-none">
          <div className="absolute inset-0 flex items-center justify-center">
            <PathGraph
              nodes={nodes}
              collections={collections}
              collectionVideos={collectionVideos}
              selectedPathId={null}
              isReadOnly={true}
            />
          </div>
        </div>
      ) : (
        <div className="w-full h-[200px] bg-muted text-muted-foreground flex items-center justify-center text-sm">
          No Cover
        </div>
      )}

      <CardHeader className="p-4">
        <h3 className="font-medium text-lg">{title}</h3>
      </CardHeader>
    </Card>
  );
};

export default PathPreview;